import { createContext, useContext } from "react";

import type { HostedSession, UsageWindow } from "./hosted-api.js";
import { text } from "./i18n.js";
import { ACCOUNT } from "./text/app.js";

/**
 * The signed-in account, as the sign-in gate hands it to the app for the
 * account menu and Settings. Null where there is no account: a box on the
 * user's own computer, which has nobody to sign out and no plan to meter.
 */
export interface AccountState {
  readonly session: HostedSession;
  /** Asks the service again, for fresh usage when the menu opens. */
  readonly refresh: () => Promise<void>;
  readonly logout: () => Promise<void>;
  /** Backs up the workspace and starts it again; the page waits on the gate's status screen meanwhile. */
  readonly restart: () => Promise<void>;
}

export const AccountContext = createContext<AccountState | null>(null);

export function useAccount(): AccountState | null {
  return useContext(AccountContext);
}

/** How much of a window is spent, as a whole percentage from 0 to 100. */
export function percentUsed(window: UsageWindow): number {
  if (window.limit <= 0) return window.spent > 0 ? 100 : 0;
  return Math.max(0, Math.min(100, Math.round((window.spent / window.limit) * 100)));
}

/** "4.2 of 10 credits": credits come to the tenth. */
export function creditsUsed(window: UsageWindow): string {
  const credits = (value: number): string => (Number.isInteger(value) ? String(value) : value.toFixed(1));
  return text(ACCOUNT).creditsUsed(credits(window.spent), credits(window.limit));
}

/** The part of an address before the @, for the name at the foot; the whole of anything else. */
export function displayName(username: string): string {
  const at = username.indexOf("@");
  return at > 0 ? username.slice(0, at) : username;
}
